import { useCallback, useEffect, useRef } from "react";
import { SCENES } from "@/data/scenes";
import { useExperience } from "@/context/ExperienceContext";

const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));
const sceneAt = (t) => {
  let i = 0;
  while (i < SCENES.length - 1 && t >= SCENES[i + 1].start) i++;
  return i;
};
const START_KEYS = ["ArrowDown", "PageDown", " ", "Spacebar", "Enter"];

// First interaction starts the commercial; afterwards playback time drives scenes, clock and colours.
export function usePlaybackStory({ videoRef, stageRef, storyRef, mobile, reduced, onText }) {
  const ex = useExperience();
  const exRef = useRef(ex);
  exRef.current = ex;
  const st = useRef({ text: null, hour: null, finale: -1, scene: -1 });

  const start = useCallback(() => {
    const e = exRef.current;
    if (e.started) return;
    e.setStarted(true);
    videoRef.current?.play().catch(() => {});
  }, [videoRef]);

  const finaleTop = useCallback(() => storyRef.current.offsetTop + storyRef.current.offsetHeight, [storyRef]);

  // Playback time → scene, clock, text, edge effects
  useEffect(() => {
    let raf = 0;
    const tick = () => {
      raf = requestAnimationFrame(tick);
      const v = videoRef.current;
      if (!v) return;
      const e = exRef.current;
      const t = v.currentTime;
      const idx = sceneAt(t);
      const sc = SCENES[idx];
      const next = SCENES[idx + 1];
      const u = clamp((t - sc.start) / (sc.end - sc.start));
      const hour = next ? sc.hour + (next.hour - sc.hour) * clamp((u - 0.8) / 0.2) : sc.hour;
      const textOn = !e.started || mobile ? true : idx === SCENES.length - 1 ? u > 0.06 : u > 0.06 && u < 0.95;
      const edge = !e.started || mobile || reduced ? 0 : u < 0.08 && idx > 0 ? 1 - u / 0.08 : u > 0.92 && next ? (u - 0.92) / 0.08 : 0;
      stageRef.current?.style.setProperty("--edge", edge.toFixed(3));
      if (idx !== st.current.scene) { st.current.scene = idx; e.setSceneIndex(idx); }
      const qh = Math.round(hour * 4) / 4;
      if (qh !== st.current.hour) { st.current.hour = qh; e.setHour(qh); }
      if (textOn !== st.current.text) { st.current.text = textOn; onText(textOn); }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [mobile, reduced, videoRef, stageRef, onText]);

  // Scroll → finale (video shrinks, quiz rises). Never traps the visitor.
  useEffect(() => {
    let raf = 0;
    const compute = () => {
      const story = storyRef.current;
      if (!story) return;
      const finale = clamp(window.scrollY / story.offsetHeight);
      stageRef.current?.style.setProperty("--finale", finale.toFixed(3));
      const qf = Math.round(finale * 20) / 20;
      if (qf !== st.current.finale) { st.current.finale = qf; exRef.current.setFinale(qf); }
      if (window.scrollY > 10) start();
    };
    const onScroll = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(compute); };
    compute();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => { window.removeEventListener("scroll", onScroll); window.removeEventListener("resize", onScroll); cancelAnimationFrame(raf); };
  }, [storyRef, stageRef, start]);

  // First scroll / swipe / key starts the commercial (and is consumed so the page stays on the video).
  useEffect(() => {
    let y0 = null;
    const atTop = () => window.scrollY < 10 && !exRef.current.started && !exRef.current.modal;
    const onWheel = (e) => { if (atTop() && e.deltaY > 0) { e.preventDefault(); start(); } };
    const onKey = (e) => { if (atTop() && START_KEYS.includes(e.key) && e.target === document.body) { e.preventDefault(); start(); } };
    const onTouchStart = (e) => { y0 = e.touches[0].clientY; };
    const onTouchMove = (e) => {
      if (!atTop() || y0 == null) return;
      if (y0 - e.touches[0].clientY > 0) e.preventDefault();
    };
    const onTouchEnd = (e) => {
      if (y0 == null) return;
      const dy = y0 - e.changedTouches[0].clientY;
      y0 = null;
      if (atTop() && dy > 20) start();
    };
    window.addEventListener("wheel", onWheel, { passive: false });
    window.addEventListener("keydown", onKey);
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    window.addEventListener("touchend", onTouchEnd, { passive: true });
    return () => {
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
    };
  }, [start]);

  // End of commercial: settle on the poster and glide into the quiz.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    let timer = 0;
    const onEnded = () => {
      const y = window.scrollY;
      timer = setTimeout(() => {
        if (exRef.current.modal || Math.abs(window.scrollY - y) > 20 || window.scrollY >= finaleTop() - 10) return;
        window.scrollTo({ top: finaleTop(), behavior: reduced ? "auto" : "smooth" });
      }, 1200);
    };
    v.addEventListener("ended", onEnded);
    return () => { v.removeEventListener("ended", onEnded); clearTimeout(timer); };
  }, [videoRef, finaleTop, reduced, mobile]);

  // Sound: never restarts — unmutes from the current position (and starts the story if needed).
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !ex.soundOn;
    if (ex.soundOn) start();
  }, [ex.soundOn, videoRef, start, mobile]);

  // Clock menu: jump to a scene and resume playback from there.
  useEffect(() => {
    ex.jumpRef.current = (i) => {
      const v = videoRef.current;
      if (!v) return;
      v.currentTime = SCENES[i].start + 0.05;
      exRef.current.setStarted(true);
      v.play().catch(() => {});
      if (window.scrollY > 0) window.scrollTo({ top: 0, behavior: reduced ? "auto" : "smooth" });
    };
  }, [ex.jumpRef, videoRef, reduced]);

  return start;
}
